import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const loginCredentialsAdapter = createEntityAdapter({
    selectId: (customer) => customer._id,
});

// Async Thunks
export const fetchLoginCredentials = createAsyncThunk(
    "loginCredentials/fetch",
    async ({ userId }, { rejectWithValue }) => {
        try {
            console.log("Fetching login credentials for userId:", userId);
            const response = await request({
                endpoint: "/admin/customer/getLoginCredentials",
                method: "GET",
                params: { userId },
            });

            console.log("API Response:", response); // Debugging log

            if (!response) {
                return rejectWithValue("No response received from server");
            }

            if (response.error) {
                return rejectWithValue(response.error.message || "Server returned an error");
            }

            if (!response.data) {
                return rejectWithValue("No data received from server");
            }

            // Extract user and customersEmployees
            const { user, customersEmployees } = response.data;

            // Ensure both are valid
            if (!user || typeof user !== "object") {
                return rejectWithValue("Invalid user data format received");
            }

            if (!Array.isArray(customersEmployees)) {
                return rejectWithValue("Invalid employees data format received");
            }

            // Return combined data
            return { user, customersEmployees };

        } catch (error) {
            return rejectWithValue(error?.message || "An unexpected error occurred while fetching customers");
        }
    }
);


export const saveLoginCredentials = createAsyncThunk(
    "loginCredentials/save",
    async ({ name, email, phone, address, employeeId }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/customer/addCustomer",
                method: "POST",
                body: {
                    name,
                    email,
                    phone,
                    address,
                    employeeId, // Associate employee with customer
                },
            });

            if (!response.success || !response.data) {
                return rejectWithValue(response.error?.message || "Failed to save customer.");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error?.message || "An unexpected error occurred while saving the customer.");
        }
    }
);

export const removeLoginCredentials = createAsyncThunk(
    "loginCredentials/remove",
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/customer/removeCustomer",
                method: "DELETE",
                params: { id },
            });

            if (!response.success) {
                return rejectWithValue(response.error?.message || "Failed to remove customer.");
            }

            return id;
        } catch ({ error }) {
            return rejectWithValue(error?.message || "An unexpected error occurred while removing the customer.");
        }
    }
);

// Initial state
const initialState = loginCredentialsAdapter.getInitialState({
    loading: false,
    error: null,
});

// Slice
const loginCredentialsSlice = createSlice({
    name: "loginCredentials",
    initialState,
    reducers: {
        clearLoginCredentials: (state) => {
            loginCredentialsAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
        },
        blockCustomer: (state, action) => {
            const customer = state.entities[action.payload];
            if (customer) {
                customer.isBlocked = !customer.isBlocked;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLoginCredentials.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLoginCredentials.fulfilled, (state, action) => {
                loginCredentialsAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchLoginCredentials.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(saveLoginCredentials.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveLoginCredentials.fulfilled, (state, action) => {
                loginCredentialsAdapter.upsertOne(state, action.payload);
                state.loading = false;
            })
            .addCase(saveLoginCredentials.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(removeLoginCredentials.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeLoginCredentials.fulfilled, (state, action) => {
                loginCredentialsAdapter.removeOne(state, action.payload);
                state.loading = false;
            })
            .addCase(removeLoginCredentials.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Selectors
export const {
    selectAll: selectAllLoginCredentials,
    selectById: selectLoginCredentialsById,
    selectIds: selectLoginCredentialsIds,
} = loginCredentialsAdapter.getSelectors((state) => state.loginCredentials);

// Actions & Reducer Export
export const { clearLoginCredentials, blockCustomer } = loginCredentialsSlice.actions;
export default loginCredentialsSlice.reducer;
